import { createFileRoute } from "@tanstack/react-router";
import { LocalServicePage } from "@/components/LocalServicePage";
import __apexTheater from "@/assets/Apex_Theater.webp.asset.json";
const hero = __apexTheater.url;
import apexTheaterRichImg from "@/assets/Apex_Theater.webp.asset.json";
import { localBusinessSchema, NEIGHBORHOOD_GEO } from "@/lib/localBusinessSchema";

const NEIGHBORHOOD = "Cherry Creek";
const SLUG = "cherry-creek";
const ZIP = "80206";
const URL = "https://apexav.com/cherry-creek/home-theater-installation";

const faqs = [
  {
    q: "How much does a Cherry Creek home theater installation cost?",
    a: "Cherry Creek projects typically start around $35,000 for a dedicated room with 4K laser projection, immersive surround, motorized screen, and acoustic treatment. The Hilltop and Polo Club estate builds we do most often run $80,000–$300,000+ with full Dolby Atmos engineering, tiered seating, and integrated lighting and shading. Every project is fixed-scope and fixed-bid before any work starts.",
  },
  {
    q: "Can you install a theater in a Cherry Creek North townhome basement?",
    a: "Yes — the lower levels in Cherry Creek North townhomes are some of our favorite spaces to work in. Standard 8-foot ceilings still support a 9.4.4 Atmos system with in-ceiling height channels and a short-throw 4K projector or 100\"+ display. We handle HOA-friendly soundproofing so movie night doesn't bleed into the neighbor's wall.",
  },
  {
    q: "Do you handle Cherry Creek historic homes (Hilltop, Polo Club)?",
    a: "Frequently. Hilltop and Polo Club estates have the ceiling height, structural mass, and footprint for true reference theaters — but they also have plaster walls, hardwood, and architectural details we will not damage. We work with your architect and interior designer to integrate the theater invisibly: acoustically transparent fabric walls, hidden in-wall speakers, and equipment racks tucked into mechanical closets.",
  },
  {
    q: "Will my new theater work with my existing smart home system?",
    a: "Yes. Most Cherry Creek homes we touch already have a control system in place. We add the new theater as another zone — same remote, same app, same scenes — and tune the lighting and shade triggers around your existing programming.",
  },
  {
    q: "How long does a Cherry Creek theater install take from sign-off to first movie night?",
    a: "Plan on 8–14 weeks for a dedicated room: 2–3 weeks of design and engineering, 4–8 weeks for construction (framing, soundproofing, finishes), and 2–3 weeks for AV install, calibration, and programming. Media-room installs without construction typically wrap in 3–5 weeks.",
  },
];

const testimonials = [
  { quote: "Apex turned the lower level of our Hilltop home into a true reference theater. The acoustic panels are completely invisible behind the fabric walls and the projection is breathtaking.", name: "Sarah L.", location: "Hilltop, Cherry Creek" },
  { quote: "We did a 9.4.6 Atmos build in our Polo Club estate. From sightlines to seat layout to the starfield ceiling — every detail was thought through.", name: "David K.", location: "Polo Club" },
  { quote: "Our Cherry Creek North townhome basement is now the best room in the house. One button drops the screen, dims the lights, and fires the projector.", name: "Megan R.", location: "Cherry Creek North" },
];

export const Route = createFileRoute("/cherry-creek/home-theater-installation")({
  head: () => ({
    meta: [
      { title: "Home Theater Installation Cherry Creek | Apex AV Denver" },
      { name: "description", content: "Custom home theater design & installation in Cherry Creek, Hilltop, and Polo Club. Reference-grade Dolby Atmos theaters from $35K. Free in-home consultation." },
      { name: "keywords", content: "home theater installation Cherry Creek, custom home theater Cherry Creek, Hilltop home theater, Polo Club home theater installer, Dolby Atmos Cherry Creek, dedicated theater room Cherry Creek, media room installation Cherry Creek, 4K projector installation Cherry Creek" },
      { property: "og:title", content: "Home Theater Installation Cherry Creek — Apex AV" },
      { property: "og:description", content: "Reference-grade home theater design & installation in Cherry Creek." },
      { property: "og:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { property: "og:image:alt", content: "Apex AV custom home theater installation in Denver, Colorado" },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { name: "twitter:image", content: `https://apexav.com${apexTheaterRichImg.url}` },
      { name: "twitter:title", content: "Home Theater Installation Cherry Creek — Apex AV" },
      { name: "twitter:description", content: "Reference-grade home theater design & installation in Cherry Creek." },
    ],
    links: [{ rel: "canonical", href: URL }, { rel: "preload", as: "image", href: hero, fetchPriority: "high" } as never],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              name: `Home Theater Installation in ${NEIGHBORHOOD}`,
              description: `Custom home theater design and installation in ${NEIGHBORHOOD}, Denver, CO.`,
              url: URL,
              provider: { "@id": "https://apexav.com/#organization" },
              areaServed: { "@type": "Place", name: `${NEIGHBORHOOD}, Denver, CO ${ZIP}` },
              serviceType: "Custom Home Theater Installation",
              offers: { "@type": "Offer", priceCurrency: "USD", price: "35000" },
            },
            localBusinessSchema({
              neighborhood: NEIGHBORHOOD,
              url: URL,
              image: `https://apexav.com${apexTheaterRichImg.url}`,
              zip: ZIP,
              latitude: NEIGHBORHOOD_GEO[SLUG].latitude,
              longitude: NEIGHBORHOOD_GEO[SLUG].longitude,
              priceFloor: 35000,
              serviceName: "Home Theater Installation",
            }),
            {
              "@type": "FAQPage",
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            },
          ],
        }),
      },
    ],
  }),
  component: () => (
    <LocalServicePage
      neighborhood={NEIGHBORHOOD}
      neighborhoodSlug={SLUG}
      zip={ZIP}
      service="home theater installation"
      serviceKey="home-theater"
      parentServiceLabel="home theater"
      parentServiceTo="/services/home-theater"
      h1="Home Theater Installation in Cherry Creek, CO"
      intro="Reference-grade dedicated theaters and media rooms designed for Cherry Creek estates and townhomes — from Hilltop and Polo Club to Cherry Creek North. Dolby Atmos surround, 4K and 8K laser projection, motorized screens, and acoustic treatment that disappears behind the architecture."
      hero={hero}
      bullets={[
        "Cherry Creek-specific HOA-friendly soundproofing",
        "9.4.6 Dolby Atmos design & calibration",
        "4K and 8K laser projection (JVC, Sony, Barco)",
        "Hidden in-wall speakers and equipment racks",
        "Integrated lighting and automation scenes",
        "Free in-home design consultation",
      ]}
      microNeighborhoods={["Hilltop", "Polo Club", "Cherry Creek North", "Cherry Creek East", "Country Club", "Belcaro"]}
      testimonials={testimonials}
      faqs={faqs}
      bodyParagraphs={[
        {
          heading: "Built for Cherry Creek architecture",
          body: (
            <>
              <p>
                Cherry Creek homes range from 1920s-era Hilltop and Country Club estates with plaster walls and ornate millwork, to new-construction Polo Club builds with vaulted basements, to 4-story Cherry Creek North townhomes with HOA noise rules. A theater design that works in one will fail in another — which is why we engineer every Cherry Creek install around the specific structure, footprint, and finish palette.
              </p>
              <p>
                Our typical Cherry Creek project starts with a free in-home walkthrough where we measure the room, listen to how you actually watch (sports? movies? concerts?), and review your existing control system. From there we deliver a fixed-scope design — drawings, equipment list, finish schedule, and price — before any construction begins.
              </p>
            </>
          ),
        },
        {
          heading: "What a Cherry Creek theater install includes",
          body: (
            <>
              <p>
                A reference-grade Cherry Creek theater is more than a projector and speakers. We design and install the full system: object-based <strong>Dolby Atmos surround</strong> (typically 7.4.4 or 9.4.6), <strong>4K or 8K laser projection</strong> from JVC, Sony, or Barco, motorized screens from Stewart or Screen Innovations, acoustic treatment hidden behind acoustically transparent fabric walls, tiered theater seating with bass shakers, fiber-optic starfield ceilings, and one-button scenes that dim the lights, drop the shades, and fire the projector.
              </p>
              <p>
                For Hilltop and Polo Club estates we frequently spec full <strong>acoustic engineering</strong> — decoupled walls, RSIC clips, double drywall, isolated HVAC ducting — so the theater performs at reference SPL without bleeding sound to the rest of the house. In Cherry Creek North townhomes we focus on HOA-friendly soundproofing that keeps the neighbors happy without compromising performance.
              </p>
            </>
          ),
        },
        {
          heading: "Integrated with the rest of your Cherry Creek smart home",
          body: (
            <>
              <p>
                Most Cherry Creek homes we touch already have a smart home system installed. We don't replace it — we extend it. The theater becomes another zone on your existing remote and app, with scenes triggered from the same keypads you already use. Lights, shades, climate, security, and AV all respond to one button.
              </p>
              <p>
                If you don't have a control system yet, we'll spec the right platform for the rest of your home as part of the same project — see our <a href="/cherry-creek/smart-home-installation" className="text-brand-text underline underline-offset-2">Cherry Creek smart home installation</a> page for the full scope.
              </p>
            </>
          ),
        },
      ]}
    />
  ),
});
